import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  Autocomplete,
  Card,
  Typography,
  Grid,
  IconButton,
  AlertTitle,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MAIN_URL } from "../../data/const";

// Official RTO / R&LA registration codes of Himachal Pradesh.
// Source: HP Transport Department "Registration Codes" list (updated 08/01/2020).
// `code` is what follows the "HP" state code on the number plate.
const hpRtoCodes = [
  // Bilaspur
  { code: "23", office: "Ghumarwin", district: "Bilaspur" },
  { code: "24", office: "Bilaspur", district: "Bilaspur" },
  { code: "69", office: "RTO Bilaspur", district: "Bilaspur" },
  { code: "89", office: "Jhandutta", district: "Bilaspur" },
  { code: "91", office: "Naina Devi Ji at Swarghat", district: "Bilaspur" },
  // Chamba
  { code: "44", office: "Churah (Tissa)", district: "Chamba" },
  { code: "45", office: "Pangi", district: "Chamba" },
  { code: "46", office: "Bharmaur", district: "Chamba" },
  { code: "47", office: "Dalhousie", district: "Chamba" },
  { code: "48", office: "Chamba", district: "Chamba" },
  { code: "57", office: "Chowari (Bhattiyat)", district: "Chamba" },
  { code: "73", office: "RTO Chamba", district: "Chamba" },
  { code: "81", office: "Salooni", district: "Chamba" },
  // Hamirpur
  { code: "21", office: "Barsar", district: "Hamirpur" },
  { code: "22", office: "Hamirpur", district: "Hamirpur" },
  { code: "55", office: "Nadaun", district: "Hamirpur" },
  { code: "67", office: "RTO Hamirpur", district: "Hamirpur" },
  { code: "74", office: "Bhoranj", district: "Hamirpur" },
  { code: "84", office: "Sujanpur", district: "Hamirpur" },
  // Kangra
  { code: "36", office: "Dehra", district: "Kangra" },
  { code: "37", office: "Palampur", district: "Kangra" },
  { code: "38", office: "Nurpur", district: "Kangra" },
  { code: "39", office: "Dharamshala", district: "Kangra" },
  { code: "40", office: "Kangra", district: "Kangra" },
  { code: "53", office: "Baijnath", district: "Kangra" },
  { code: "54", office: "Jawali", district: "Kangra" },
  { code: "56", office: "Jaisinghpur", district: "Kangra" },
  { code: "68", office: "RTO Kangra (at Dharamshala)", district: "Kangra" },
  { code: "83", office: "Jawalamukhi", district: "Kangra" },
  { code: "88", office: "Fatehpur", district: "Kangra" },
  { code: "90", office: "Shahpur", district: "Kangra" },
  { code: "94", office: "Nagrota Bagwan", district: "Kangra" },
  { code: "96", office: "Dheera", district: "Kangra" },
  { code: "97", office: "Indora", district: "Kangra" },
  // Kinnaur
  { code: "25", office: "Kalpa", district: "Kinnaur" },
  { code: "26", office: "Nichar", district: "Kinnaur" },
  { code: "27", office: "Pooh", district: "Kinnaur" },
  // Kullu
  { code: "34", office: "Kullu", district: "Kullu" },
  { code: "35", office: "Anni", district: "Kullu" },
  { code: "49", office: "Banjar", district: "Kullu" },
  { code: "58", office: "Manali", district: "Kullu" },
  { code: "66", office: "RTO Kullu", district: "Kullu" },
  // Lahaul & Spiti
  { code: "41", office: "Kaza", district: "Lahaul & Spiti" },
  { code: "42", office: "Keylong", district: "Lahaul & Spiti" },
  { code: "43", office: "Udaipur", district: "Lahaul & Spiti" },
  // Mandi
  { code: "28", office: "Sarkaghat", district: "Mandi" },
  { code: "29", office: "Jogindernagar", district: "Mandi" },
  { code: "30", office: "Karsog", district: "Mandi" },
  { code: "31", office: "Sundernagar", district: "Mandi" },
  { code: "32", office: "Gohar", district: "Mandi" },
  { code: "33", office: "Mandi Sadar", district: "Mandi" },
  { code: "65", office: "RTO Mandi", district: "Mandi" },
  { code: "76", office: "Padhar", district: "Mandi" },
  { code: "82", office: "Balh", district: "Mandi" },
  { code: "86", office: "Dharampur", district: "Mandi" },
  { code: "87", office: "Thunag", district: "Mandi" },
  // Shimla
  { code: "03", office: "Shimla (Urban)", district: "Shimla" },
  { code: "06", office: "Rampur", district: "Shimla" },
  { code: "08", office: "Chopal", district: "Shimla" },
  { code: "09", office: "Theog", district: "Shimla" },
  { code: "10", office: "Rohru", district: "Shimla" },
  { code: "51", office: "Shimla (Rural)", district: "Shimla" },
  { code: "62", office: "STA Shimla", district: "Shimla" },
  { code: "63", office: "RTO Shimla", district: "Shimla" },
  { code: "77", office: "Dodra Kawar", district: "Shimla" },
  { code: "92", office: "RTO Rampur", district: "Shimla" },
  { code: "95", office: "Kumarsain", district: "Shimla" },
  // Sirmaur
  { code: "16", office: "Rajgarh", district: "Sirmaur" },
  { code: "16AA", office: "Pachhad", district: "Sirmaur" },
  { code: "17", office: "Paonta Sahib", district: "Sirmaur" },
  { code: "18", office: "Nahan", district: "Sirmaur" },
  { code: "71", office: "RTO Sirmaur (at Nahan)", district: "Sirmaur" },
  { code: "79", office: "Sangrah", district: "Sirmaur" },
  { code: "85", office: "Shillai", district: "Sirmaur" },
  // Solan
  { code: "11", office: "Arki", district: "Solan" },
  { code: "12", office: "Nalagarh", district: "Solan" },
  { code: "13", office: "Kandaghat", district: "Solan" },
  { code: "14", office: "Solan", district: "Solan" },
  { code: "15", office: "Parwanoo", district: "Solan" },
  { code: "64", office: "RTO Solan", district: "Solan" },
  { code: "93", office: "RTO Nalagarh", district: "Solan" },
  // Una
  { code: "19", office: "Amb", district: "Una" },
  { code: "19AA", office: "Gagret", district: "Una" },
  { code: "20", office: "Una", district: "Una" },
  { code: "72", office: "RTO Una", district: "Una" },
  { code: "78", office: "Bangana", district: "Una" },
  { code: "80", office: "Haroli", district: "Una" },
];

// Sub-office codes such as HP-16AA / HP-19AA already carry letters, so the
// separate series letter is optional for them.
const hasLetterSuffix = (code) => /[A-Za-z]/.test(code || "");

const vehicleTypes = [
  { value: "car", label: "Car", icon: <LocalTaxiIcon /> },
  { value: "bus", label: "Bus", icon: <DirectionsBusIcon /> },
  { value: "volvo", label: "Volvo", icon: <AirportShuttleIcon /> },
];

const VehicleForm = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [isLoading, setIsLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showHelp, setShowHelp] = useState(false);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!user?._id) {
      setNotification({
        open: true,
        message: "Authentication error. Please login again.",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: values.name,
        vehicleNum:
          `${values.prefix}${values.district}${values.series}${values.number}`.toUpperCase(),
        id: user._id,
        type: values.type,
      };

      const response = await axios.post(
        `${MAIN_URL}/api/v1/vehicle/register`,
        payload
      );

      setNotification({
        open: true,
        message: response.data.message || "Vehicle added successfully!",
        severity: "success",
      });

      resetForm();

      setTimeout(() => {
        navigate("/invoices");
      }, 2000);
    } catch (error) {
      console.error("Vehicle registration error:", error);

      let errorMessage = "Failed to add vehicle. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;

        // Make error messages more user-friendly
        if (errorMessage.includes("already exists")) {
          errorMessage = `Vehicle number ${values.prefix}${values.district}${values.series}${values.number} is already registered. Please check your vehicle number.`;
        } else if (errorMessage.includes("duplicate")) {
          errorMessage =
            "This vehicle is already registered in the system. Please verify your vehicle number.";
        }
      }

      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch vehicles data
  useEffect(() => {
    const fetchVehicles = async () => {
      if (!user?._id) return;

      try {
        setIsLoadingVehicles(true);
        const response = await axios.get(
          `${MAIN_URL}/api/v1/owner/${user._id}/vehicles`
        );
        setVehicles(response.data.vehicles || []);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        setNotification({
          open: true,
          message: "Failed to load vehicles data",
          severity: "error",
        });
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchVehicles();
  }, [user, api]);

  // Stats Card Component
  const StatsCard = () => (
    <Card
      sx={{
        p: 3,
        display: "flex",
        alignItems: "center",
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      <DirectionsBusIcon
        sx={{
          fontSize: 40,
          mr: 2,
          color: "secondary.main",
        }}
      />
      <Box>
        <Typography variant="h6" color="text.primary">
          Total Vehicles
        </Typography>
        {isLoadingVehicles ? (
          <CircularProgress size={24} />
        ) : (
          <Typography variant="h4" color="secondary.main">
            {vehicles.length}
          </Typography>
        )}
      </Box>
    </Card>
  );

  // Vehicle Number Preview Component
  const VehicleNumberPreview = ({ values }) => {
    const fullNumber =
      `${values.prefix}${values.district}${values.series}${values.number}`.toUpperCase();
    const isComplete =
      values.district &&
      (values.series || hasLetterSuffix(values.district)) &&
      values.number.length === 4;

    return (
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mt: 3,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.grey[800]
              : theme.palette.grey[50],
          border: (theme) =>
            isComplete
              ? `2px solid ${theme.palette.success.main}`
              : `2px dashed ${theme.palette.grey[400]}`,
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          gutterBottom
          textAlign="center"
        >
          Vehicle Number Preview
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80px",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "#ffffff",
            borderRadius: 2,
            border: "3px solid #000",
            padding: "16px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Indian Flag colors at the top */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "8px",
              background:
                "linear-gradient(to right, #FF9933 33%, #ffffff 33%, #ffffff 66%, #138808 66%)",
            }}
          />

          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Courier New', monospace",
              fontWeight: 700,
              letterSpacing: "0.15em",
              color: "#000",
              textAlign: "center",
            }}
          >
            {fullNumber || "HP--"}
          </Typography>
        </Box>
        {isComplete && (
          <Typography
            variant="caption"
            color="success.main"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            ✓ Vehicle number is complete
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.primary[500]
            : theme.palette.grey[100],
      }}
    >
      {/* Header Section with Stats */}
      <Box mb={4}>
        <Header
          title="Register Your Vehicle"
          subtitle="Add a new vehicle to your fleet"
        />
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={4}>
            <StatsCard />
          </Grid>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                p: 3,
                backgroundColor: (theme) => theme.palette.background.paper,
              }}
            >
              <Typography variant="h6" color="text.primary" gutterBottom>
                Quick Tips
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Make sure to have your vehicle registration documents ready. All
                vehicles must comply with Himachal Pradesh transport
                regulations.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Main Form Section */}
      <Card
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          backgroundColor: (theme) => theme.palette.background.paper,
          boxShadow: 3,
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={{
            name: "",
            prefix: "HP",
            district: "",
            series: "",
            number: "",
            type: "",
          }}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: 2,
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography variant="h5" color="primary" gutterBottom>
                      Basic Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter the vehicle's basic details
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="filled"
                    label="Vehicle Name"
                    placeholder="e.g., MyBus2024"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    name="name"
                    error={!!touched.name && !!errors.name}
                    helperText={
                      touched.name && errors.name
                        ? errors.name
                        : "No spaces allowed. Use CamelCase (e.g., MyBus2024)"
                    }
                  />
                </Grid>

                {/* Vehicle Number Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: 2,
                      borderColor: "primary.main",
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography variant="h5" color="primary">
                        Vehicle Registration Number
                      </Typography>
                      <IconButton
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ ml: 1 }}
                        size="small"
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Enter the HP registration number details
                    </Typography>
                  </Box>

                  {showHelp && (
                    <Alert
                      severity="info"
                      sx={{ mb: 3 }}
                      onClose={() => setShowHelp(false)}
                    >
                      <AlertTitle>Registration Number Format</AlertTitle>
                      <Typography variant="body2">
                        <strong>Format:</strong> HP-XX-X-XXXX (e.g.,
                        HP-31-A-1234)
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        <li>
                          <strong>HP:</strong> State code (fixed)
                        </li>
                        <li>
                          <strong>XX:</strong> RTO code of the office that
                          registered the vehicle. Search by code or town name -
                          all {hpRtoCodes.length} Himachal Pradesh RTO / R&amp;LA
                          codes are listed.
                        </li>
                        <li>
                          <strong>X:</strong> Series letter(s), 1-2 letters
                          (A-Z). Optional for sub-office codes such as HP-16AA
                          and HP-19AA.
                        </li>
                        <li>
                          <strong>XXXX:</strong> Vehicle number (0001-9999)
                        </li>
                      </Box>
                    </Alert>
                  )}

                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={4} sm={3} md={1.5}>
                      <TextField
                        fullWidth
                        variant="filled"
                        value="HP"
                        label="State"
                        disabled
                        sx={{
                          "& .MuiInputBase-input": {
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            textAlign: "center",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={8} sm={9} md={4.5}>
                      <Autocomplete
                        options={hpRtoCodes}
                        groupBy={(option) => option.district}
                        getOptionLabel={(option) =>
                          `${option.code} - ${option.office}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.code === value.code
                        }
                        value={
                          hpRtoCodes.find(
                            (option) => option.code === values.district
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue(
                            "district",
                            newValue ? newValue.code : ""
                          );
                          setFieldTouched("district", true, false);
                        }}
                        onBlur={() => setFieldTouched("district", true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="filled"
                            label="RTO Code"
                            placeholder="Search code or town"
                            error={!!touched.district && !!errors.district}
                            helperText={
                              (touched.district && errors.district) ||
                              "e.g. 31 - Sundernagar"
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={5} sm={4} md={2.5}>
                      <TextField
                        fullWidth
                        variant="filled"
                        label="Series"
                        name="series"
                        placeholder="A"
                        value={values.series}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!touched.series && !!errors.series}
                        helperText={
                          (touched.series && errors.series) ||
                          (hasLetterSuffix(values.district) ? "Optional" : "")
                        }
                        inputProps={{
                          maxLength: 2,
                          style: {
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            textAlign: "center",
                            textTransform: "uppercase",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={7} sm={5} md={3}>
                      <TextField
                        fullWidth
                        variant="filled"
                        label="Number"
                        name="number"
                        placeholder="1234"
                        value={values.number}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!touched.number && !!errors.number}
                        helperText={touched.number && errors.number}
                        inputProps={{
                          maxLength: 4,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          style: {
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            textAlign: "center",
                            letterSpacing: "0.1em",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Vehicle Number Preview */}
                  <VehicleNumberPreview values={values} />
                </Grid>

                {/* Vehicle Type Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: 2,
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography variant="h5" color="primary" gutterBottom>
                      Vehicle Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select the type of vehicle
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {vehicleTypes.map((type) => (
                      <Grid item xs={12} sm={6} md={4} key={type.value}>
                        <Card
                          onClick={() =>
                            handleChange({
                              target: { name: "type", value: type.value },
                            })
                          }
                          sx={{
                            p: 3,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            transform:
                              values.type === type.value
                                ? "scale(1.05)"
                                : "scale(1)",
                            border: (theme) =>
                              values.type === type.value
                                ? `3px solid ${theme.palette.primary.main}`
                                : `2px solid ${theme.palette.divider}`,
                            backgroundColor: (theme) =>
                              values.type === type.value
                                ? theme.palette.mode === "dark"
                                  ? theme.palette.primary.dark
                                  : theme.palette.primary.light
                                : theme.palette.background.paper,
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: 6,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {React.cloneElement(type.icon, {
                              sx: {
                                fontSize: "4rem",
                                color:
                                  values.type === type.value
                                    ? "primary.main"
                                    : "text.secondary",
                                transition: "color 0.3s ease",
                              },
                            })}
                            <Typography
                              variant="h6"
                              fontWeight={
                                values.type === type.value ? 700 : 500
                              }
                              color={
                                values.type === type.value
                                  ? "primary"
                                  : "text.primary"
                              }
                            >
                              {type.label}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {touched.type && errors.type && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ mt: 2, ml: 2 }}
                    >
                      {errors.type}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Divider sx={{ my: 4 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/invoices")}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    px: 6,
                    py: 1.5,
                    minWidth: "200px",
                    position: "relative",
                  }}
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={24} sx={{ color: "inherit" }} />
                    </Box>
                  ) : (
                    <>
                      <DirectionsBusIcon sx={{ mr: 1 }} />
                      Add Vehicle
                    </>
                  )}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Card>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Vehicle name is required")
    .matches(/^\S*$/, "Name should not contain spaces"),
  district: yup
    .string()
    .required("RTO code is required")
    .oneOf(
      hpRtoCodes.map((option) => option.code),
      "Select a valid Himachal Pradesh RTO code"
    ),
  series: yup.string().when("district", {
    // HP-16AA / HP-19AA style sub-office codes already carry letters.
    is: (district) => hasLetterSuffix(district),
    then: yup
      .string()
      .matches(/^[A-Za-z]{0,2}$/, "Series must be 1-2 letters (A-Z)"),
    otherwise: yup
      .string()
      .required("Series is required")
      .matches(/^[A-Za-z]{1,2}$/, "Series must be 1-2 letters (A-Z)"),
  }),
  number: yup
    .string()
    .required("Number is required")
    .matches(/^\d{4}$/, "Must be exactly 4 digits (e.g., 1234)"),
  type: yup.string().required("Please select a vehicle type"),
});

export default VehicleForm;
